import { useState, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  UploadCloud, X, FileText, Image as ImageIcon, CheckCircle2, ChevronLeft, ChevronRight,
  Loader2, User, Home, Bug, ClipboardList, Copy, PartyPopper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Overline } from "@/components/Primitives";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PROPERTY_TYPES, PEST_TYPES, SEVERITIES, TIME_SLOTS, SERVICES } from "@/data/site";

const STEPS = [
  { icon: User, label: "Your Info" },
  { icon: Home, label: "Property" },
  { icon: Bug, label: "Pest Details" },
  { icon: UploadCloud, label: "Photos" },
  { icon: ClipboardList, label: "Review" },
];

const ACCEPT = ".jpg,.jpeg,.png,.pdf,.heic,.heif";

export default function RequestInspection() {
  const [params] = useSearchParams();
  const preService = params.get("service");
  const preName = SERVICES.find((s) => s.slug === preService)?.name;

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "",
    property_type: "", pest_type: "", severity: "", preferred_date: "", preferred_time: "",
    affected_area: "", indoor_outdoor: "", rooms_affected: "", prior_treatments: "", pets: "", children: "", additional_notes: preName ? `Requesting: ${preName}` : "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = useCallback((list) => {
    const incoming = Array.from(list).map((file) => ({
      id: Math.random().toString(36).slice(2), file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setFiles((prev) => [...prev, ...incoming]);
  }, []);

  const onDrop = (e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };
  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const validateStep = () => {
    if (step === 0) {
      if (!form.full_name || !form.email || !form.phone || !form.address) { toast.error("Please complete all contact fields."); return false; }
    }
    if (step === 1 && !form.property_type) { toast.error("Please select a property type."); return false; }
    if (step === 2 && (!form.pest_type || !form.severity)) { toast.error("Please select pest type and severity."); return false; }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      let photos = [];
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f.file));
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photos = up.data.files;
      }
      const payload = {
        full_name: form.full_name, email: form.email, phone: form.phone, address: form.address,
        property_type: form.property_type, pest_type: form.pest_type, severity: form.severity,
        preferred_date: form.preferred_date, preferred_time: form.preferred_time,
        property_details: {
          affected_area: form.affected_area, indoor_outdoor: form.indoor_outdoor, rooms_affected: form.rooms_affected,
          prior_treatments: form.prior_treatments, pets: form.pets, children: form.children, additional_notes: form.additional_notes,
        },
        photos,
      };
      const res = await api.post("/inspections", payload);
      setDone(res.data.reference_number);
      toast.success("Request submitted! Check your email for confirmation.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="pt-36 pb-24 min-h-screen">
        <div className="max-w-xl mx-auto px-4 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-[2rem] p-10 border border-black/5 shadow-xl">
            <div className="h-20 w-20 rounded-full bg-brand-green flex items-center justify-center mx-auto"><PartyPopper className="h-10 w-10 text-brand-gold" /></div>
            <h1 className="font-heading text-3xl font-extrabold text-brand-green mt-6">Request Received!</h1>
            <p className="text-brand-muted mt-3">Thank you! A licensed technician will contact you shortly. A confirmation email is on its way.</p>
            <div className="bg-brand-light rounded-2xl p-6 mt-6 border border-black/5">
              <p className="text-xs uppercase tracking-wider text-brand-muted font-semibold">Your Reference Number</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span data-testid="reference-number" className="font-heading text-2xl font-extrabold text-brand-green">{done}</span>
                <button onClick={() => { navigator.clipboard.writeText(done); toast.success("Copied!"); }} className="text-brand-muted hover:text-brand-green"><Copy className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Link to="/"><Button variant="outline" className="rounded-full">Back to Home</Button></Link>
              <Link to="/register"><Button className="rounded-full bg-brand-green text-white hover:bg-brand-greenHover">Track in Portal</Button></Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const fieldSelect = (label, key, options, testid) => (
    <div>
      <Label className="text-brand-charcoal">{label}</Label>
      <Select value={form[key]} onValueChange={(v) => set(key, v)}>
        <SelectTrigger data-testid={testid} className="mt-2 h-12 rounded-xl"><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="pt-32 pb-24 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <Overline>Free Inspection Request</Overline>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-brand-green tracking-tight mt-3">Tell us about your pest problem</h1>
          <p className="text-brand-muted mt-3">Takes about 2 minutes. Upload photos so our technicians can prepare before they arrive.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-10">
          {STEPS.map((s, i) => (
            <div key={s.label} className="flex-1 flex items-center">
              <div className="flex flex-col items-center flex-1">
                <div className={`h-11 w-11 rounded-full flex items-center justify-center transition-colors duration-300 ${i <= step ? "bg-brand-green text-brand-gold" : "bg-white border border-black/10 text-brand-muted"}`}>
                  {i < step ? <CheckCircle2 className="h-5 w-5 text-brand-gold" /> : <s.icon className="h-5 w-5" />}
                </div>
                <span className={`text-[11px] mt-2 font-medium hidden sm:block ${i <= step ? "text-brand-green" : "text-brand-muted"}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 -mt-5 sm:-mt-6 ${i < step ? "bg-brand-green" : "bg-black/10"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-[2rem] p-8 border border-black/5 shadow-sm">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              {step === 0 && (
                <div className="space-y-5">
                  <h3 className="font-heading text-xl font-bold text-brand-green">Customer Information</h3>
                  <div><Label className="text-brand-charcoal">Full Name *</Label><Input data-testid="insp-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="Jane Doe" /></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-brand-charcoal">Email *</Label><Input data-testid="insp-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="jane@email.com" /></div>
                    <div><Label className="text-brand-charcoal">Phone *</Label><Input data-testid="insp-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="(555) 123-4567" /></div>
                  </div>
                  <div><Label className="text-brand-charcoal">Property Address *</Label><Input data-testid="insp-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="123 King George Blvd, Surrey, BC" /></div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <h3 className="font-heading text-xl font-bold text-brand-green">Property Type</h3>
                  {fieldSelect("Property Type *", "property_type", PROPERTY_TYPES, "insp-property-type")}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {fieldSelect("Indoor or Outdoor", "indoor_outdoor", ["Indoor", "Outdoor", "Both"], "insp-indoor-outdoor")}
                    <div><Label className="text-brand-charcoal">Approx. Affected Area</Label><Input data-testid="insp-affected-area" value={form.affected_area} onChange={(e) => set("affected_area", e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="e.g. Kitchen & garage" /></div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div><Label className="text-brand-charcoal">Rooms Affected</Label><Input data-testid="insp-rooms" value={form.rooms_affected} onChange={(e) => set("rooms_affected", e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="e.g. 2" /></div>
                    {fieldSelect("Pets on Property?", "pets", ["Yes", "No"], "insp-pets")}
                    {fieldSelect("Children in Home?", "children", ["Yes", "No"], "insp-children")}
                  </div>
                  {fieldSelect("Prior Treatments Performed?", "prior_treatments", ["Yes", "No", "Not sure"], "insp-prior")}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <h3 className="font-heading text-xl font-bold text-brand-green">Pest & Scheduling</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {fieldSelect("Pest Type *", "pest_type", PEST_TYPES, "insp-pest-type")}
                    {fieldSelect("Severity *", "severity", SEVERITIES, "insp-severity")}
                  </div>
                  {form.severity === "Emergency" && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">For emergencies, please also call <a href="tel:16723775455" className="font-bold underline">+1 (672) 377-5455</a> for same-day response.</div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-brand-charcoal">Preferred Service Date</Label><Input data-testid="insp-date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className="mt-2 h-12 rounded-xl" /></div>
                    {fieldSelect("Preferred Time", "preferred_time", TIME_SLOTS, "insp-time")}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5">
                  <h3 className="font-heading text-xl font-bold text-brand-green">Upload Photos</h3>
                  <p className="text-brand-muted text-sm">Add photos of pest activity, damage, nests, or affected rooms. Supports JPG, PNG, PDF, HEIC.</p>
                  <div
                    data-testid="upload-dropzone"
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => inputRef.current?.click()}
                    className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors duration-200 ${dragging ? "border-brand-green bg-brand-green/5" : "border-black/15 hover:border-brand-green/40"}`}
                  >
                    <UploadCloud className="h-12 w-12 text-brand-green mx-auto" />
                    <p className="font-semibold text-brand-green mt-4">Drag & drop files here</p>
                    <p className="text-brand-muted text-sm mt-1">or click to browse</p>
                    <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" data-testid="upload-input"
                      onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }} />
                  </div>
                  {files.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="upload-previews">
                      {files.map((f) => (
                        <div key={f.id} className="relative group rounded-xl overflow-hidden border border-black/5 bg-brand-light aspect-square">
                          {f.preview ? <img src={f.preview} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex flex-col items-center justify-center text-brand-muted p-2"><FileText className="h-7 w-7" /><span className="text-[10px] mt-1 truncate w-full text-center">{f.file.name}</span></div>}
                          <button onClick={(e) => { e.stopPropagation(); removeFile(f.id); }} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5">
                  <h3 className="font-heading text-xl font-bold text-brand-green">Review & Additional Notes</h3>
                  <div><Label className="text-brand-charcoal">Additional Notes</Label><Textarea data-testid="insp-notes" value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} className="mt-2 rounded-xl min-h-[100px]" placeholder="Anything else our technicians should know?" /></div>
                  <div className="bg-brand-light rounded-2xl p-6 border border-black/5 space-y-2 text-sm">
                    {[["Name", form.full_name], ["Email", form.email], ["Phone", form.phone], ["Address", form.address], ["Property", form.property_type], ["Pest", form.pest_type], ["Severity", form.severity], ["Preferred", [form.preferred_date, form.preferred_time].filter(Boolean).join(" · ")], ["Photos", `${files.length} file(s)`]].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-charcoal text-right">{v || "—"}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between mt-8 pt-6 border-t border-black/5">
            <Button data-testid="insp-back-btn" variant="outline" onClick={back} disabled={step === 0} className="rounded-full">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="insp-next-btn" onClick={next} className="rounded-full bg-brand-green text-white hover:bg-brand-greenHover font-semibold">
                Continue <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button data-testid="insp-submit-btn" onClick={submit} disabled={submitting} className="rounded-full bg-brand-gold text-brand-green font-bold hover:bg-brand-goldHover">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Submit Request</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
